import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend, CartesianGrid,
  ScatterChart, Scatter, ZAxis,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { getRiTripEconomics } from '../../../api/auth';
import { siteCategoryColor } from '../lib/driveBucket';
import { normalizeSiteCategory } from '../lib/siteCategory';
import { headroomInsight } from '../lib/insights';
import { simulateRateImpact } from '../lib/rateSimulator';
import { RiLoading, RiEmpty, RiError, InsightCard } from '../primitives/States';
import MetricCard from '../primitives/MetricCard';
import { useReload } from '../lib/loadState';
import { fmtUsd } from '../../../utils/format';
import { guestDisplayName } from '../lib/labelFormat';
import { RI_TABLE_FRAME, RI_THEAD, RI_TH, RI_ROW, RI_TD } from '../lib/tableStyles';

function ScatterPointTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
      {p.name && <div className="font-semibold mb-1">{p.name}</div>}
      <div>Distance: <span className="font-medium">{Math.round(p.x)} mi</span></div>
      <div>Rate headroom: <span className="font-medium">{p.y}%</span></div>
      <div>Site cost: <span className="font-medium">{fmtUsd(p.z, { cents: true })}</span></div>
    </div>
  );
}

// Tracks the viewport width so chart label widths/fonts/truncation can scale
// with the screen. Same pattern as CongestionTab.
function useViewportWidth() {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

export default function TripEconomicsTab({ clientId, range }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, retry] = useReload();
  const [ratePerNight, setRatePerNight] = useState(0);
  const vw = useViewportWidth();
  const yAxisWidth = vw < 640 ? 100 : vw < 1024 ? 160 : 220;
  const yAxisFont = vw < 640 ? 10 : 11;
  const maxNameChars = vw < 640 ? 14 : vw < 1024 ? 22 : 32;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getRiTripEconomics(clientId, { from, to })
      .then((r) => { if (!cancelled) { setPayload(r?.data || null); setLoading(false); } })
      .catch(() => { if (!cancelled) { setPayload(null); setFailed(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [clientId, from, to, reloadKey]);

  // v_booking_enriched stores guest names UPPERCASE; Title-Case at ingestion
  // so charts, tables, tooltips, and insight copy all read consistently
  // without titleCasing at every site. Nameless parties become "Anonymous"
  // here too — otherwise the chart's axis labels came out blank while the table
  // below (via rateSimulator) already said "Anonymous".
  const rows = useMemo(
    () => (payload?.rows || []).map((row) => ({ ...row, guest_name: guestDisplayName(row.guest_name, row.guest_name_is_fallback) })),
    [payload]
  );
  const avgGas = payload?.avg_gas;
  const avgGasIsEstimated = payload?.avg_gas_is_estimated === true;

  const topTwenty = useMemo(() => rows.slice(0, 20).map((r) => ({
    name: r.guest_name,
    site: r.site_cost,
    fuel: r.fuel_cost_roundtrip,
  })), [rows]);

  const scatterByCategory = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (r.drive_distance_miles == null || r.rate_headroom_pct == null) continue;
      const cat = normalizeSiteCategory(r.site_category);
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push({
        x: r.drive_distance_miles,
        y: r.rate_headroom_pct,
        z: r.site_cost,
        name: r.guest_name,
      });
    }
    return [...map.entries()].map(([cat, data]) => ({ cat, data }));
  }, [rows]);

  const insight = headroomInsight(rows);
  const sim = simulateRateImpact(rows, ratePerNight);

  if (loading) return <RiLoading />;
  if (failed) return <RiError onRetry={retry} />;
  if (!rows.length) return <RiEmpty message="No booked-guest trip economics in this date range." />;

  return (
    <div className="space-y-4">
      {/* 4A: stacked bar */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Trip Cost Breakdown (Top 20 by Guest Trip Investment)</h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Gas: ${avgGas?.toFixed(2) ?? '—'}/gal {avgGasIsEstimated
              ? <span title="No station prices found for this property — using the $3.50 default" className="text-amber-700 dark:text-amber-300">(default — no station data)</span>
              : '(property)'}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Per-guest stacked cost (site + fuel) for the 20 guests with the highest total trip investment.</p>
        <div className="w-full" style={{ height: Math.max(420, topTwenty.length * 32 + 60) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topTwenty} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={yAxisWidth}
                interval={0}
                tick={({ x, y, payload }) => {
                  const v = payload?.value ?? '';
                  const display = typeof v === 'string' && v.length > maxNameChars
                    ? `${v.slice(0, maxNameChars - 1).trimEnd()}…`
                    : v;
                  return (
                    <text x={x} y={y} dy={4} textAnchor="end" fontSize={yAxisFont} className="fill-gray-500 dark:fill-gray-400">
                      {display}
                    </text>
                  );
                }}
              />
              <ThemedChartTooltip formatter={(v) => fmtUsd(v, { cents: true })} />
              <Legend />
              <Bar dataKey="site" name="Site cost" stackId="a" fill="#10B981" />
              <Bar dataKey="fuel" name="Fuel (round trip)" stackId="a" fill="#F97316" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 4B: scatter */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Distance vs Rate Headroom</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Each dot is a booking — drive distance vs. nightly rate. Helps spot guests willing to pay more for longer drives.</p>
        <div style={{ height: 340 }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
              <XAxis type="number" dataKey="x" name="Miles" tick={{ fontSize: 11 }} />
              <YAxis type="number" dataKey="y" name="Rate Headroom %" tick={{ fontSize: 11 }} />
              <ZAxis type="number" dataKey="z" range={[40, 220]} />
              <ThemedChartTooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={<ScatterPointTooltip />}
                wrapperStyle={{ pointerEvents: 'none' }}
              />
              <Legend />
              {scatterByCategory.map(({ cat, data }) => (
                <Scatter key={cat} name={cat} data={data} fill={siteCategoryColor(cat)} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        {insight && (
          <div className="mt-3">
            <InsightCard title="Insight">
              Long-haul (&gt;300 mi) guests have <strong>{insight.longHaulAvg}%</strong> rate headroom on average, vs <strong>{insight.shortAvg}%</strong> for short-haul (&lt;100 mi) — a <strong>{insight.delta > 0 ? '+' : ''}{insight.delta} pt</strong> spread.
            </InsightCard>
          </div>
        )}
      </div>

      {/* 4C: simulator */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Rate Impact Simulator</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Model how a nightly-rate change shifts bookings and revenue, based on observed drive-distance price elasticity.</p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
          <strong>GTI</strong> = Guest Trip Investment — the total a guest spends to make the trip happen
          (site cost + fuel cost). Higher GTI means a more committed guest.
        </p>
        <div className="flex items-center gap-3 mb-3">
          <input type="range" min={0} max={50} step={1} value={ratePerNight}
            onChange={(e) => setRatePerNight(Number(e.target.value))} className="flex-1" />
          <div className="text-sm font-medium text-gray-900 dark:text-white w-20 text-right">
            +${ratePerNight}/night
          </div>
        </div>
        <RateImpactSummary sim={sim} />

        {sim.rows.length > 0 && (
          <>
            <div className="overflow-x-auto responsive-card-table-wrapper">
              <div className={RI_TABLE_FRAME}>
                <table className="w-full text-sm min-w-[640px] responsive-card-table">
                <thead className={RI_THEAD}>
                  <tr>
                    <th className={`${RI_TH} text-left`}>Guest</th>
                    <th className={`${RI_TH} text-right`} title="Guest Trip Investment — site cost + fuel cost at today's rate">Current GTI</th>
                    <th className={`${RI_TH} text-right`} title="Guest Trip Investment after the simulated rate change">New GTI</th>
                    <th className={`${RI_TH} text-right`}>Change</th>
                    <th className={`${RI_TH} text-right`}>Impact</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 dark:text-gray-300">
                  {sim.rows.slice(0, 20).map((r, i) => (
                    <tr key={i} className={RI_ROW}>
                      <td className={`${RI_TD} text-gray-900 dark:text-white max-[499px]:flex-col max-[499px]:items-start rc-label-feature`} data-label="Guest">
                        <span className="max-[499px]:w-full max-[499px]:text-center">
                          {r.guest_name}
                          {r.drive_distance_miles != null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400"> ({Math.round(Number(r.drive_distance_miles)).toLocaleString()} mi)</span>
                          )}
                        </span>
                      </td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="Current GTI">{fmtUsd(r.currentGti, { cents: true })}</td>
                      <td className={`${RI_TD} text-right tabular-nums`} data-label="New GTI">{fmtUsd(r.newGti, { cents: true })}</td>
                      <td className={`${RI_TD} text-right tabular-nums text-emerald-600 dark:text-emerald-400`} data-label="Change">+{fmtUsd(r.change, { cents: true })}</td>
                      <td className={`${RI_TD} text-right tabular-nums font-medium`} data-label="Impact">{r.pct > 0 ? '+' : ''}{r.pct}%</td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </div>
            {sim.rows.length > 20 && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Showing top 20 of {sim.rows.length} guests (sorted by Guest Trip Investment).
              </div>
            )}
            {ratePerNight > 0 && sim.rows.length >= 2 && (() => {
              const sorted = [...sim.rows].sort((a, b) => a.pct - b.pct);
              const low = sorted[0];
              const high = sorted[sorted.length - 1];
              if (low.pct === high.pct) return null;
              const ratio = low.pct > 0 ? (high.pct / low.pct).toFixed(1) : null;
              return (
                <div className="mt-3">
                  <InsightCard title="Sensitivity spread">
                    <strong>{low.guest_name}</strong> barely notices this increase ({low.pct}%) — fuel dominates their trip.
                    {' '}<strong>{high.guest_name}</strong> feels it {ratio ? `${ratio}× more` : 'most'} ({high.pct}%).
                  </InsightCard>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The four headline figures of the rate simulator, as the same tinted cards the
 * headline strip above uses, each with its own colour (purple, blue, amber, green) on
 * a left-edge bar so no two read alike. Dollar values are whole dollars: the simulator rounds
 * every figure, so ".00" only added noise at this size. Averages are derived from
 * the totals shown, and are left out when there are no guests rather than dividing
 * by zero.
 * @param {{ sim: { guests: number, oldGti: number, newGti: number, change: number, pct: number } }} props
 */
export function RateImpactSummary({ sim }) {
  const raised = sim.change > 0;
  const avgPerGuest = (total) => (sim.guests > 0 ? `${fmtUsd(total / sim.guests)} avg per guest` : null);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <MetricCard
        label="Guests"
        value={sim.guests.toLocaleString()}
        hint="booked guests with trip data"
        tone="purple"
        icon="users"
        accent="left"
      />
      <MetricCard
        label="Current GTI"
        value={fmtUsd(sim.oldGti)}
        hint={avgPerGuest(sim.oldGti)}
        tone="brandBlue"
        icon="dollar"
        accent="left"
      />
      <MetricCard
        label="New GTI"
        value={fmtUsd(sim.newGti)}
        hint={avgPerGuest(sim.newGti)}
        tone="amber"
        icon="dollar"
        accent="left"
      />
      <MetricCard
        label="Impact"
        value={`${raised ? '+' : ''}${fmtUsd(sim.change)}`}
        hint={raised ? `${sim.pct > 0 ? '+' : ''}${sim.pct}% on current GTI` : 'Move the slider to model a nightly increase'}
        tone="emerald"
        icon="trendingUp"
        accent="left"
      />
    </div>
  );
}
